import React, { useState, useEffect } from 'react';
import { useI18n } from '../../context/I18nContext';
import { GRCControl, GRCDocument, GRCEvidence } from '../../types/grc';
import { grcService } from '../../services/grcService';
import {
  X, Link as LinkIcon, FileText, Upload, Search,
  Check, Plus, ExternalLink, Clock, CheckCircle,
  AlertCircle, File, Trash2, Eye, Download, Loader2
} from 'lucide-react';

// Types for evidence management (kept for backward compat with LinkedEvidence display)
export interface Evidence {
  id: number;
  type: 'policy' | 'document' | 'screenshot' | 'log' | 'certificate' | 'report' | 'other';
  name: string;
  description?: string;
  file_url?: string;
  uploaded_at: string;
  uploaded_by: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  expiry_date?: string;
  linked_controls: number[];
  tags?: string[];
  control_code?: string;
  control_title?: string;
}

export interface Policy {
  id: number;
  name: string;
  description?: string;
  status: 'draft' | 'active' | 'archived' | 'review' | 'approved' | 'retired';
  version: string;
  created_at: string;
  updated_at: string;
  file_url?: string;
  framework_id?: number;
}

interface EvidenceLinkingModalProps {
  control: GRCControl;
  frameworkId: number;
  isOpen: boolean;
  onClose: () => void;
  onLink: (controlId: number, evidenceIds: number[]) => void;
}

export const EvidenceLinkingModal: React.FC<EvidenceLinkingModalProps> = ({
  control,
  frameworkId,
  isOpen,
  onClose,
  onLink
}) => {
  const { t, language } = useI18n();
  const [activeTab, setActiveTab] = useState<'policies' | 'evidence' | 'upload'>('policies');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<number[]>(control.linked_policy_ids || []);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [evidence, setEvidence] = useState<Evidence[]>([]);
  const [loadingPolicies, setLoadingPolicies] = useState(true);
  const [loadingEvidence, setLoadingEvidence] = useState(true);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<Evidence['type']>('document');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);

  // Load policies (documents) for this framework from API
  useEffect(() => {
    if (!isOpen || !frameworkId) return;
    setLoadingPolicies(true);
    grcService.getDocuments(frameworkId).then(docs => {
      // Map GRCDocument to Policy shape
      const mapped: Policy[] = docs.map(d => ({
        id: d.id,
        name: d.title,
        description: d.content?.substring(0, 100) || '',
        status: d.status === 'approved' ? 'active' : d.status as any,
        version: d.version || '1.0',
        created_at: '',
        updated_at: '',
        framework_id: d.framework_id ?? undefined,
      }));
      setPolicies(mapped);
    }).catch(() => setPolicies([])).finally(() => setLoadingPolicies(false));
  }, [isOpen, frameworkId]);

  // Load evidence for this framework from API
  useEffect(() => {
    if (!isOpen || !frameworkId) return;
    setLoadingEvidence(true);
    grcService.getFrameworkEvidence(frameworkId).then(evs => {
      const mapped: Evidence[] = evs.map(e => ({
        id: e.id,
        type: (e as any).evidence_type || 'document',
        name: (e as any).description || (e as any).file_path || `Evidence #${e.id}`,
        description: (e as any).description || '',
        file_url: (e as any).file_path || '',
        uploaded_at: (e as any).uploaded_at || '',
        uploaded_by: (e as any).uploaded_by || 'System',
        status: (e as any).status || 'pending',
        linked_controls: [(e as any).control_id],
        control_code: (e as any).control_code,
        control_title: (e as any).control_title,
      }));
      setEvidence(mapped);
    }).catch(() => setEvidence([])).finally(() => setLoadingEvidence(false));
  }, [isOpen, frameworkId]);

  // Filter items by search
  const filteredPolicies = policies.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEvidence = evidence.filter(e => 
    e.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelection = (id: number) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSave = () => {
    onLink(control.id, selectedItems);
    onClose();
  };

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const result = await grcService.addEvidence(control.id, {
        file: uploadFile,
        description: uploadDescription || uploadFile.name,
        uploaded_by: 'Admin',
      });
      if (result.success) {
        // Reload evidence list
        const evs = await grcService.getFrameworkEvidence(frameworkId);
        const mapped: Evidence[] = evs.map(e => ({
          id: e.id,
          type: (e as any).evidence_type || 'document',
          name: (e as any).description || (e as any).file_path || `Evidence #${e.id}`,
          description: (e as any).description || '',
          file_url: (e as any).file_path || '',
          uploaded_at: (e as any).uploaded_at || '',
          uploaded_by: (e as any).uploaded_by || 'System',
          status: (e as any).status || 'pending',
          linked_controls: [(e as any).control_id],
          control_code: (e as any).control_code,
          control_title: (e as any).control_title,
        }));
        setEvidence(mapped);
        setUploadFile(null);
        setUploadDescription('');
        setActiveTab('evidence');
      } else {
        alert(result.error || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed: ' + String(err));
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status: Evidence['status'] | Policy['status']) => {
    const config = {
      approved: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', icon: CheckCircle },
      active: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', icon: CheckCircle },
      pending: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', icon: Clock },
      draft: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', icon: FileText },
      rejected: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', icon: AlertCircle },
      expired: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', icon: AlertCircle },
      archived: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-500 dark:text-gray-400', icon: File },
    };
    const { bg, text, icon: Icon } = config[status] || config.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}>
        <Icon className="w-3 h-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTypeIcon = (type: Evidence['type']) => {
    const icons = {
      policy: FileText,
      document: File,
      screenshot: Eye,
      log: FileText,
      certificate: CheckCircle,
      report: FileText,
      other: File,
    };
    const Icon = icons[type] || File;
    return <Icon className="w-4 h-4" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-indigo-600" />
              {t.proof.linkEvidence}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {control.control_code}: {control.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6 flex-shrink-0">
          {[
            { key: 'policies', label: t.proof.policies, icon: FileText },
            { key: 'evidence', label: t.proof.existingEvidence, icon: File },
            { key: 'upload', label: t.proof.uploadNew, icon: Upload },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors
                ${activeTab === tab.key
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }
              `}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        {activeTab !== 'upload' && (
          <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t.actions.search}
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'policies' && (
            <div className="space-y-2">
              {loadingPolicies ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  {language === 'sr' ? 'Učitavanje politika...' : 'Loading policies...'}
                </div>
              ) : filteredPolicies.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Nema dostupnih politika' : 'No policies available'}
                </div>
              ) : (
                filteredPolicies.map(policy => (
                  <label
                    key={policy.id}
                    className={`
                      flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all
                      ${selectedItems.includes(policy.id)
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(policy.id)}
                      onChange={() => toggleSelection(policy.id)}
                      className="sr-only"
                    />
                    <div className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center mr-4 flex-shrink-0
                      ${selectedItems.includes(policy.id)
                        ? 'border-indigo-500 bg-indigo-500'
                        : 'border-gray-300 dark:border-gray-600'
                      }
                    `}>
                      {selectedItems.includes(policy.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="w-10 h-10 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      <FileText className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {policy.name}
                        </span>
                        {getStatusBadge(policy.status)}
                      </div>
                      {policy.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                          {policy.description}
                        </p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        <span>v{policy.version}</span>
                        <span>•</span>
                        <span>{new Date(policy.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        // Open policy preview
                      }}
                      className="p-2 text-gray-400 hover:text-indigo-600 ml-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </label>
                ))
              )}
            </div>
          )}

          {activeTab === 'evidence' && (
            <div className="space-y-2">
              {loadingEvidence ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                  {language === 'sr' ? 'Učitavanje dokaza...' : 'Loading evidence...'}
                </div>
              ) : filteredEvidence.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {language === 'sr' ? 'Nema dostupnih dokaza' : 'No evidence available'}
                </div>
              ) : (
                filteredEvidence.map(ev => (
                  <label
                    key={ev.id}
                    className={`
                      flex items-center p-4 rounded-xl border-2 cursor-pointer transition-all
                      ${selectedItems.includes(1000 + ev.id)
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(1000 + ev.id)}
                      onChange={() => toggleSelection(1000 + ev.id)}
                      className="sr-only"
                    />
                    <div className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center mr-4 flex-shrink-0
                      ${selectedItems.includes(1000 + ev.id)
                        ? 'border-indigo-500 bg-indigo-500'
                        : 'border-gray-300 dark:border-gray-600'
                      }
                    `}>
                      {selectedItems.includes(1000 + ev.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                      {getTypeIcon(ev.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {ev.name}
                        </span>
                        {getStatusBadge(ev.status)}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        <span className="capitalize">{ev.type}</span>
                        <span>•</span>
                        <span>{ev.uploaded_by}</span>
                        <span>•</span>
                        <span>{new Date(ev.uploaded_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          // Preview evidence
                        }}
                        className="p-2 text-gray-400 hover:text-indigo-600"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          // Download evidence
                        }}
                        className="p-2 text-gray-400 hover:text-indigo-600"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </label>
                ))
              )}
            </div>
          )}

          {activeTab === 'upload' && (
            <div className="space-y-6">
              {/* Dropzone */}
              <div
                className={`
                  border-2 border-dashed rounded-xl p-8 text-center transition-colors
                  ${uploadFile 
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400'
                  }
                `}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file) setUploadFile(file);
                }}
              >
                {uploadFile ? (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-xl flex items-center justify-center mx-auto">
                      <File className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{uploadFile.name}</p>
                      <p className="text-sm text-gray-500">{(uploadFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button
                      onClick={() => setUploadFile(null)}
                      className="text-red-600 hover:text-red-700 text-sm flex items-center gap-1 mx-auto"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t.actions.remove}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center mx-auto">
                      <Upload className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {language === 'sr' ? 'Prevucite fajl ovde' : 'Drag and drop a file here'}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {language === 'sr' ? 'ili' : 'or'}
                      </p>
                    </div>
                    <label className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium cursor-pointer transition-colors">
                      <Plus className="w-4 h-4" />
                      {t.proof.selectFile}
                      <input
                        type="file"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) setUploadFile(file);
                        }}
                        className="sr-only"
                      />
                    </label>
                  </div>
                )}
              </div>

              {/* Evidence type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.proof.evidenceType}
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { type: 'document' as const, label: 'Document' },
                    { type: 'screenshot' as const, label: 'Screenshot' },
                    { type: 'log' as const, label: 'Log' },
                    { type: 'certificate' as const, label: 'Certificate' },
                    { type: 'report' as const, label: 'Report' },
                    { type: 'other' as const, label: 'Other' },
                  ].map(item => (
                    <button
                      key={item.type}
                      onClick={() => setUploadType(item.type)}
                      className={`
                        px-3 py-2 rounded-lg text-sm font-medium transition-colors
                        ${uploadType === item.type
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-2 border-indigo-500'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-2 border-transparent hover:border-gray-300'
                        }
                      `}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t.actions.description}
                </label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder={language === 'sr' ? 'Opišite ovaj dokaz...' : 'Describe this evidence...'}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Upload button */}
              <button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? (language === 'sr' ? 'Otpremanje...' : 'Uploading...') : t.proof.uploadEvidence}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {selectedItems.length} {language === 'sr' ? 'stavki izabrano' : 'items selected'}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg"
            >
              {t.actions.cancel}
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
            >
              <LinkIcon className="w-4 h-4" />
              {t.proof.linkSelected}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Linked Evidence display component
interface LinkedEvidenceProps {
  control: GRCControl;
  onManageClick: () => void;
}

export const LinkedEvidence: React.FC<LinkedEvidenceProps> = ({ control, onManageClick }) => {
  const { t, language } = useI18n();
  const linkedCount = control.linked_policy_ids?.length || 0;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
          <LinkIcon className="w-4 h-4" />
          {t.proof.linkedEvidence}
        </h4>
        <button
          onClick={onManageClick}
          className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1"
        >
          <Plus className="w-3 h-3" />
          {t.proof.manage}
        </button>
      </div>

      {linkedCount === 0 ? (
        <div className="text-center py-6 text-gray-400">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">
            {language === 'sr' ? 'Nema povezanih dokaza' : 'No linked evidence'}
          </p>
          <button
            onClick={onManageClick}
            className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            {language === 'sr' ? '+ Dodaj dokaz' : '+ Add evidence'}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Show linked policy/evidence IDs */}
          {control.linked_policy_ids?.slice(0, 3).map(id => (
              <div key={id} className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <FileText className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 truncate">
                  {language === 'sr' ? 'Dokaz' : 'Evidence'} #{id}
                </span>
              </div>
          ))}
          {linkedCount > 3 && (
            <button
              onClick={onManageClick}
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              +{linkedCount - 3} {language === 'sr' ? 'više' : 'more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default EvidenceLinkingModal;
